import { DOCUMENT } from '@angular/common';
import { computed, inject } from '@angular/core';
import { getExhibitionSettings, getLanguage, getOperatorsSettings, getSupportedLanguages } from '@app/app.reducer';
import { FooterState, LegalLink, LegalLinkKey, LegalLinks } from '@app/layout/footer/data-access/models/footer.model';
import { Store } from '@ngrx/store';
import { HelperService } from '@store/helpers/helper.service';
import { UserService } from '@store/user/user.service';
import { filter as _filter, map as _map, isEmpty } from 'lodash';

export class FooterFacade {
  readonly #document = inject(DOCUMENT);
  readonly #store = inject(Store);
  readonly #userService = inject(UserService);
  readonly #helperService = inject(HelperService);

  readonly state = this.initState();

  changeLanguage(selectedLanguage: string) {
    this.#userService.changePreferredLanguage(selectedLanguage);
    this.#helperService.setLanguage(selectedLanguage);
  }

  private initState() {
    const year = new Date().getFullYear();
    const appVersion = this.#document.getElementById('app-version')?.getAttribute('app-version');

    const currentLanguage = this.#store.selectSignal(getLanguage);
    const supportedLanguages = this.#store.selectSignal(getSupportedLanguages);

    const legalLinks = this.setLegalLinks();

    return computed<FooterState>(() => ({
      year,
      appVersion,
      legalLinks: legalLinks(),
      supportedLanguages: supportedLanguages(),
      currentLanguage: currentLanguage()
    }));
  }

  private setLegalLinks() {
    const operatorSettings = this.#store.selectSignal(getOperatorsSettings);
    const exhibitionSettings = this.#store.selectSignal(getExhibitionSettings);

    return computed<LegalLinks>(() => {
      const operator = operatorSettings();
      const exhibition = exhibitionSettings();

      if (isEmpty(operator) && isEmpty(exhibition)) {
        return [];
      }

      const { isImprintVisible, imprintUrl, isPrivacyLinkVisible, privacyUrl, isTermsVisible, termsUrl } =
        exhibition ?? operator;

      return _map(
        _filter(
          [
            { key: LegalLinkKey.Imprint, isVisible: isImprintVisible, url: imprintUrl },
            { key: LegalLinkKey.Privacy, isVisible: isPrivacyLinkVisible, url: privacyUrl },
            { key: LegalLinkKey.Terms, isVisible: isTermsVisible, url: termsUrl }
          ],
          ({ isVisible }) => isVisible
        ),
        ({ key, url }): LegalLink => ({ key, url })
      );
    });
  }
}
